/**
 * Plan sidebar panel showing current plan status and subtask progress.
 * Displayed in right drawer alongside Files and Debug panels.
 * Resizable via drag handle.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ListChecks,
  X,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  Loader2,
  XCircle,
  Clock,
  Target,
  FileText,
} from 'lucide-react';
import type {
  PlanningStrategy,
  OrchestratedStatusDict,
  ResearchStatusDict,
  OrchestratedSubtaskStatus,
} from '../../types/planning';

type PlanStatusType = OrchestratedStatusDict | ResearchStatusDict | null;

interface PlanSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  strategy: PlanningStrategy | null;
  status: PlanStatusType;
  isPlanActive: boolean;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}

function SubtaskStateIcon({ state }: { state: string }) {
  switch (state) {
    case 'completed':
    case 'done':
      return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    case 'in_progress':
      return <Loader2 className="w-4 h-4 text-sky-500 animate-spin" />;
    case 'abandoned':
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-400" />;
    default:
      return <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600" />;
  }
}

function SubtaskItem({
  subtask,
  index,
  isExpanded,
  onToggle,
}: {
  subtask: OrchestratedSubtaskStatus;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const isCurrent = subtask.is_current;
  const isCompleted = subtask.state === 'completed' || subtask.state === 'done';
  const attempt = subtask.latestAttempt;
  const attemptTone =
    attempt?.state === 'failed'
      ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
      : attempt?.state === 'succeeded'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
        : 'border-gray-100 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300';

  return (
    <div
      className={`border rounded-lg transition-all ${
        isCurrent
          ? 'border-sky-200 dark:border-sky-800 bg-sky-50/50 dark:bg-sky-900/20'
          : isCompleted
            ? 'border-emerald-100 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-900/10'
            : 'border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800/50'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-3 text-left"
      >
        <div className="flex-shrink-0 mt-0.5">
          <SubtaskStateIcon state={subtask.state} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 font-mono">
              #{index + 1}
            </span>
            {isCurrent && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 rounded">
                CURRENT
              </span>
            )}
          </div>
          <p
            className={`text-sm font-medium mt-0.5 ${
              isCompleted
                ? 'text-gray-500 dark:text-gray-400'
                : 'text-gray-800 dark:text-gray-100'
            }`}
          >
            {subtask.name || subtask.description}
          </p>
        </div>
        {attempt && attempt.number > 0 && (
          <span className="flex-shrink-0 rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">
            {attempt.number}×
          </span>
        )}
        <div className="flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {attempt && attempt.state !== 'succeeded' && (
        <div
          data-testid="latest-attempt"
          className={`mx-3 mb-3 rounded-md border px-2.5 py-2 text-xs ${attemptTone}`}
        >
          <div className="font-medium">
            Attempt {attempt.number} {attempt.state}
          </div>
          {attempt.summary && (
            <p className="mt-1 leading-relaxed">{attempt.summary}</p>
          )}
        </div>
      )}

      {isExpanded && (
        <div className="px-3 pb-3 pt-0 space-y-2 border-t border-gray-100 dark:border-gray-700 mt-0">
          <div className="pt-2">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
              <FileText className="w-3 h-3" />
              Description
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              {subtask.description}
            </p>
          </div>

          {subtask.expected_output && (
            <div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                <Target className="w-3 h-3" />
                Expected Outcome
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                {subtask.expected_output}
              </p>
            </div>
          )}

          {subtask.result && (
            <div>
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-500 mb-1">
                <CheckCircle2 className="w-3 h-3" />
                Actual Outcome
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded">
                {subtask.result}
              </p>
            </div>
          )}

          {subtask.lastTool && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Last tool</div>
              <code className="text-[11px] text-sky-600 dark:text-sky-400">
                {subtask.lastTool}
              </code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PlanSidebar({
  isOpen,
  onClose,
  strategy,
  status,
  isPlanActive,
  isCollapsed,
  onToggleCollapsed,
}: PlanSidebarProps) {
  const [width, setWidth] = useState(360);
  const [isResizing, setIsResizing] = useState(false);
  const [expandedSubtasks, setExpandedSubtasks] = useState<Set<number>>(
    new Set(),
  );
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.max(300, Math.min(600, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const toggleSubtask = useCallback((index: number) => {
    setExpandedSubtasks((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  if (!isOpen) {
    return null;
  }

  const isOrchestrated = strategy === 'orchestrated' || strategy === 'notebook';
  const orchestratedStatus = isOrchestrated
    ? (status as OrchestratedStatusDict)
    : null;
  const subtasks = orchestratedStatus?.subtasks ?? [];

  const completedCount = subtasks.filter(
    (s) => s.state === 'completed' || s.state === 'done',
  ).length;
  const totalCount = subtasks.length;
  const progressPercent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div
      ref={sidebarRef}
      className="flex flex-col h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 relative"
      style={{ width: `${width}px` }}
      data-testid="plan-sidebar"
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-sky-400 transition-colors z-10"
        onMouseDown={handleMouseDown}
        style={{ backgroundColor: isResizing ? '#38bdf8' : 'transparent' }}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <ListChecks size={18} className="text-sky-600" />
          <span className="font-medium text-gray-900 dark:text-white">
            Plan
          </span>
          {isPlanActive && totalCount > 0 && (
            <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full font-medium">
              {completedCount}/{totalCount}
            </span>
          )}
        </div>
        <button
          onClick={onToggleCollapsed}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
          aria-label="Toggle plan content"
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </button>
        <button
          onClick={onClose}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
          title="Close panel"
        >
          <X size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {!isPlanActive || !status ? (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <ListChecks className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              No Active Plan
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-[200px]">
              When the assistant creates a plan, you'll see the progress here.
            </p>
          </div>
        ) : isCollapsed ? (
          <div className="p-4 text-sm text-gray-600 dark:text-gray-300">
            {orchestratedStatus?.outcome || orchestratedStatus?.original_task}
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Plan Overview */}
            {orchestratedStatus?.original_task && (
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1">
                  <Target className="w-3 h-3" />
                  Plan Objective
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-200">
                  {orchestratedStatus.original_task}
                </p>
              </div>
            )}

            {/* Progress Bar */}
            <div>
              <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-2">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  Progress
                </span>
                <span className="font-medium">{progressPercent}%</span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Subtasks */}
            <div>
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                Subtasks
              </h4>
              <div className="space-y-2">
                {subtasks.map((subtask, index) => (
                  <SubtaskItem
                    key={index}
                    subtask={subtask}
                    index={index}
                    isExpanded={expandedSubtasks.has(index)}
                    onToggle={() => toggleSubtask(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlanSidebar;
